import { Vector3 } from "three";
import { Context } from "../../context";
import {
  ActorOfInterest,
  DefensivePlayState,
  LocationOfInterest,
  OffensivePlayMode,
  OffensivePlayState,
  PlayStateManagerCb,
  PositionOfInterest,
} from "../../managers/playstate";
import { ControllerStrategy, CameraModeOptions } from "../strategy";
import {
  CameraLandmark,
  CameraLandmarkLookup,
  CameraMode,
  SmoothTimes,
} from "../controller";
import { OfftrackRevertControllerStrategy } from "./action";
import { getPlayerType, PlayerType } from "../../world/actor/actor";

const defaultCameraModeOptions: CameraModeOptions = {
  focusedID: 1,
};

export class PersonalControllerStrategy extends ControllerStrategy {
  private target?: ActorOfInterest | LocationOfInterest = undefined;
  private altTarget?: ActorOfInterest | LocationOfInterest = undefined;
  private homePos?: Vector3 = undefined;
  private initialised = false;
  private focusedID: number = 1;
  private playerType: PlayerType = PlayerType.PITCHER;

  private defaultAutoTrack: boolean = false;
  private defaultDamping: number = SmoothTimes.MEDIUM;

  private POIs: [
    PositionOfInterest | undefined,
    PositionOfInterest | undefined
  ] = [undefined, undefined];

  private stateUpdateCb?: PlayStateManagerCb;

  init(
    context: Context,
    options: CameraModeOptions = defaultCameraModeOptions
  ): ControllerStrategy {
    super.init(context);
    this.focusedID = options.focusedID || 1;
    this.playerType = getPlayerType(this.focusedID);

    if (!this.registered) {
      console.error("no controller registered for personal camera strategy");
      return this;
    }

    if (this.registered.controls) {
      this.defaultDamping = this.registered.controls.smoothTime;
    }

    this.defaultAutoTrack = this.registered.state.autoTrack;

    this.registered.addAuxStrategy(OfftrackRevertControllerStrategy);
    this.registered.state.autoTrack = false;

    this.target = new ActorOfInterest(context, this.focusedID);

    // Getting current play in init may cause issues with
    // position switching handedness while live
    const current = context.gameState.getCurrentPlay();
    switch (this.playerType) {
      case PlayerType.PITCHER:
        if (current?.matchup?.pitchHand.code == "L") {
          this.homePos =
            CameraLandmarkLookup[CameraLandmark.MOUND_LEFT].clone();
        } else {
          this.homePos =
            CameraLandmarkLookup[CameraLandmark.MOUND_RIGHT].clone();
        }
        this.altTarget = new LocationOfInterest(
          context,
          CameraLandmark.PITCHER_POV
        );
        break;
      case PlayerType.CATCHER: //catcher falls through to batter
      case PlayerType.BATTER:
        if (current?.matchup?.batSide.code == "L") {
          this.homePos = CameraLandmarkLookup[CameraLandmark.UMP_LEFT].clone();
        } else {
          this.homePos = CameraLandmarkLookup[CameraLandmark.UMP_RIGHT].clone();
        }
        this.altTarget = new LocationOfInterest(
          context,
          CameraLandmark.BATTER_TARGET
        );
        break;
      default:
        let loc =
          this.target.position || CameraLandmarkLookup[CameraLandmark.HH];
        this.homePos?.addVectors(
          loc,
          loc.clone().normalize().multiplyScalar(25)
        );
        this.homePos?.setY(7);
        this.altTarget = new LocationOfInterest(
          context,
          CameraLandmark.PITCHER_PLATE_MIDPOINT
        );
        break;
    }

    this.registered.state.homePos = this.homePos;
    this.POIs[0] = this.target;
    this.POIs[1] = undefined;
    this.registered.contextRef.camera.updateProjectionMatrix();

    return this;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    if (!this.registered) {
      console.error("no controls registered");
      return;
    }

    super.update(deltaS, gameTimeDelta, frameNum);

    // lazy register for play state updates here because if we do it in init, the callback
    // triggering steps on itself (setMode gets called simultaneously in different threads)
    if (!this.initialised) {
      // play state callback
      this.stateUpdateCb = (
        _defense: DefensivePlayState,
        offense: OffensivePlayState
      ) => {
        this.playStateUpdated(offense);
      };

      this.registered.setMode(CameraMode.PAIR, true);

      const playStateManager = this.registered.contextRef.playStateManager;
      playStateManager?.addSubscriber("personal-cam", this.stateUpdateCb);

      this.initialised = true;
    }
    this.registered.state.tracked = this.POIs[0]?.object;
    this.registered.state.trackedOther = this.POIs[1]?.object;

    if (frameNum != undefined) {
      this.lastFrameNum = frameNum;
    }
  }

  playStateUpdated(offense: OffensivePlayState) {
    const controller = this.registered;
    if (!controller || !controller.controls) return;

    switch (offense.mode) {
      case OffensivePlayMode.IDLE:
        controller.controls.smoothTime = SmoothTimes.SLOW;
        controller.controls.zoomTo(1, true);
        this.POIs[0] = this.target;
        this.POIs[1] = undefined;
        break;

      case OffensivePlayMode.BATTING:
        controller.controls.smoothTime = SmoothTimes.FAST;
        if (this.playerType == PlayerType.PITCHER)
          controller.controls?.zoomTo(3.5, true);
        if (
          this.playerType == PlayerType.PITCHER ||
          this.playerType == PlayerType.CATCHER ||
          this.playerType == PlayerType.BATTER
        ) {
          controller.controls.smoothTime = SmoothTimes.VERY_FAST;
          this.POIs[0] = this.altTarget;
          this.POIs[1] = undefined;
        } else {
          this.POIs[0] = this.target;
          this.POIs[1] = this.altTarget;
        }
        break;

      case OffensivePlayMode.RUNNING: {
        controller.controls.zoomTo(1, true);
        controller.controls.smoothTime = SmoothTimes.FAST;
        this.POIs[0] = this.target;
        this.POIs[1] = offense.ballOfInterest;
        break;
      }

      default:
        console.error("unsupported game state in batter cam");
        break;
    }
  }

  teardown(): void {
    super.teardown();

    if (!this.registered) return;

    if (this.registered?.controls) {
      this.registered.controls.enabled = true;
      this.registered.controls.smoothTime = this.defaultDamping;
      this.registered.controls.zoomTo(1, true);
    }

    this.registered.removeAuxStrategy(OfftrackRevertControllerStrategy);
    this.registered.state.autoTrack = this.defaultAutoTrack;

    const playStateManager = this.registered.contextRef.playStateManager;
    playStateManager?.removeSubscriber("personal-cam");
  }
}
